import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowUpCircle, ExternalLink, History, Loader2, RefreshCw, RotateCcw, Server } from 'lucide-react';
import { platformAPI } from '../../services/platformApi';

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

const errorText = (e) =>
  e?.response?.data?.error || e?.response?.data?.errors?.map((x) => `${x.path}: ${x.msg}`).join(', ') || e?.message;

const button =
  'inline-flex items-center gap-1 rounded-lg border border-neutral-300 px-3 py-1.5 text-sm text-neutral-700 hover:bg-neutral-50 disabled:opacity-50 dark:border-neutral-600 dark:text-neutral-200 dark:hover:bg-neutral-700';
const danger =
  'inline-flex items-center gap-1 rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50';
const primary =
  'inline-flex items-center gap-1 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50';
const input =
  'w-full rounded-lg border border-neutral-300 bg-white px-3 py-1.5 text-sm text-neutral-900 dark:border-neutral-600 dark:bg-neutral-900 dark:text-white';
const card = 'rounded-xl border border-neutral-200 bg-white p-4 dark:border-neutral-700 dark:bg-neutral-800';

const STATE_TEXT = {
  current: null,
  rolled_back: 'Production is serving an earlier release than the production branch. The next Promote moves it forward again.',
  mixed: 'The frontend and the backend are on different commits. A rollback or promote may have stopped half-way; finish it or roll the other half.',
  unknown: 'What the frontend or backend serves could not be read.',
};

const RUN_STYLES = {
  success: 'text-green-700 dark:text-green-400',
  failure: 'text-red-600 dark:text-red-400',
  cancelled: 'text-neutral-500',
};

function Commit({ sha, short }) {
  return <code className="rounded bg-neutral-100 px-1.5 py-0.5 text-xs dark:bg-neutral-700">{short || (sha ? sha.slice(0, 7) : '—')}</code>;
}

function runState(run) {
  if (!run) return '';
  if (run.status !== 'completed') return run.status === 'waiting' ? 'waiting for approval' : run.status.replace('_', ' ');
  return run.conclusion || 'completed';
}

/** A run being followed: polls every ten seconds until it finishes. */
function RunProgress({ runId, url, onDone }) {
  const run = useQuery({
    queryKey: ['release-run', runId],
    queryFn: () => platformAPI.releaseRun(runId),
    select: (r) => r.data.run,
    enabled: Boolean(runId),
    refetchInterval: (q) => (q.state.data?.data?.run?.status === 'completed' ? false : 10000),
  });
  const r = run.data;
  const done = r?.status === 'completed';
  // Once, when it finishes: the page re-reads what is serving.
  useEffect(() => {
    if (done) onDone?.();
  }, [done]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className={`${card} space-y-2`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-medium text-neutral-900 dark:text-white">
          {r?.status !== 'completed' && <Loader2 className="h-4 w-4 animate-spin text-primary-600" />}
          {r?.title || 'Started'}
          <span className={`font-normal ${RUN_STYLES[r?.conclusion] || 'text-neutral-500'}`}>{runState(r)}</span>
        </div>
        <a href={r?.url || url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-primary-600 hover:underline">
          Open in GitHub <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>
      {!runId && (
        <p className="text-sm text-neutral-500">The run has not appeared yet; follow it in GitHub.</p>
      )}
      {r?.jobs?.length > 0 && (
        <ul className="space-y-1 text-sm">
          {r.jobs.map((j) => (
            <li key={j.name} className="flex justify-between gap-2">
              <span className="text-neutral-700 dark:text-neutral-200">{j.name}</span>
              <span className={RUN_STYLES[j.conclusion] || 'text-neutral-500'}>{runState(j)}</span>
            </li>
          ))}
        </ul>
      )}
      {run.error && <p className="text-sm text-red-600">{errorText(run.error)}</p>}
    </div>
  );
}

function PromoteForm({ commit, onStart, pending }) {
  const [confirm, setConfirm] = useState('');
  return (
    <div className="mt-2 space-y-2 rounded-lg bg-neutral-50 p-3 dark:bg-neutral-900/40">
      <p className="text-sm text-neutral-600 dark:text-neutral-300">
        Deploys this commit to production for every tenant: backend first, then the portal. It takes about five minutes.
      </p>
      <label className="block text-sm text-neutral-700 dark:text-neutral-200">
        Type <Commit short={commit.short} /> to confirm
        <input value={confirm} onChange={(e) => setConfirm(e.target.value)} className={`${input} mt-1 font-mono`} autoComplete="off" />
      </label>
      <button type="button" className={primary} disabled={pending || confirm.trim() !== commit.short} onClick={() => onStart({ sha: commit.sha, confirm })}>
        {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUpCircle className="h-4 w-4" />}
        Promote {commit.short}
      </button>
    </div>
  );
}

function RollbackForm({ release, onStart, pending }) {
  const [scope, setScope] = useState('all');
  const [reason, setReason] = useState('');
  const [confirm, setConfirm] = useState('');
  const scopes = [
    ['all', 'Everything', 'The portal first, then the backend. About three minutes.'],
    ['frontend', 'Portal only', 'The web pages. Under a minute. Only releases promoted after build tagging was set up.'],
    ['backend', 'Backend only', 'The API, rebuilt from that commit. About three minutes.'],
  ];
  return (
    <div className="mt-2 space-y-3 rounded-lg bg-red-50 p-3 dark:bg-red-900/10">
      <div className="space-y-1">
        {scopes.map(([key, label, help]) => (
          <label key={key} className="flex items-start gap-2 text-sm text-neutral-700 dark:text-neutral-200">
            <input type="radio" name={`scope-${release.sha}`} checked={scope === key} onChange={() => setScope(key)} className="mt-1" />
            <span>
              <span className="font-medium">{label}</span> <span className="text-neutral-500">{help}</span>
            </span>
          </label>
        ))}
      </div>
      <label className="block text-sm text-neutral-700 dark:text-neutral-200">
        Why (kept in the audit log and the run)
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={500} className={`${input} mt-1`} />
      </label>
      <label className="block text-sm text-neutral-700 dark:text-neutral-200">
        Type <Commit short={release.short} /> to confirm
        <input value={confirm} onChange={(e) => setConfirm(e.target.value)} className={`${input} mt-1 font-mono`} autoComplete="off" />
      </label>
      <p className="text-xs text-neutral-500 dark:text-neutral-400">
        Every tenant moves together. The production branch is not rewound; the next Promote moves everything forward again. Data is
        not touched: the database keeps everything written since.
      </p>
      <button
        type="button"
        className={danger}
        disabled={pending || reason.trim().length < 3 || confirm.trim() !== release.short}
        onClick={() => onStart({ sha: release.sha, scope, reason: reason.trim(), confirm })}
      >
        {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
        Roll back to {release.short}
      </button>
    </div>
  );
}

/**
 * Production releases: what serves, what is waiting on staging, what it can
 * go back to. Every button starts a GitHub Actions workflow and follows it;
 * nothing here deploys from inside the API it would be replacing.
 */
export default function ReleasesPage() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(null); // `promote:<sha>` | `rollback:<sha>` | 'redeploy'
  const [active, setActive] = useState(null); // { runId, url }

  const releases = useQuery({
    queryKey: ['platform-releases'],
    queryFn: () => platformAPI.releases(),
    select: (r) => r.data,
  });

  const start = useMutation({
    mutationFn: ({ kind, body }) => platformAPI.startRelease(kind, body),
    onSuccess: (res) => {
      setActive({ runId: res.data.runId, url: res.data.url });
      setOpen(null);
      queryClient.invalidateQueries({ queryKey: ['platform-releases'] });
    },
  });
  const run = (kind) => (body) => start.mutate({ kind, body });

  const v = releases.data;
  const serving = v?.serving;
  const latestRun = v?.runs?.[0];
  const running = latestRun && latestRun.status !== 'completed';

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-neutral-900 dark:text-white">Releases</h1>
          <p className="mt-1 max-w-2xl text-sm text-neutral-500 dark:text-neutral-400">
            Promote, roll back or redeploy production. Each button starts a GitHub Actions workflow, so it keeps working even if this
            release has broken the API.
          </p>
        </div>
        <button type="button" className={button} onClick={() => releases.refetch()} disabled={releases.isFetching}>
          {releases.isFetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Refresh
        </button>
      </div>

      {releases.error && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">{errorText(releases.error)}</div>}
      {start.error && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">{errorText(start.error)}</div>}
      {releases.isLoading && (
        <div className="flex items-center gap-2 text-sm text-neutral-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Reading GitHub…
        </div>
      )}

      {v && !v.configured && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
          Release controls are not set up on this app, so this page cannot start anything. See{' '}
          <Link to="/manage/setup" className="underline">Setup → Release controls</Link>. Releases are managed from production’s console;
          staging has nothing to roll back to.
        </div>
      )}

      {serving && (
        <div className={card}>
          <div className="flex items-center gap-2 text-sm font-medium text-neutral-900 dark:text-white">
            <Server className="h-4 w-4 text-primary-600" /> Serving now
          </div>
          <dl className="mt-3 grid gap-3 text-sm sm:grid-cols-3">
            <div>
              <dt className="text-neutral-500">Backend (API)</dt>
              <dd><Commit sha={serving.backend} short={serving.backendShort} /></dd>
            </div>
            <div>
              <dt className="text-neutral-500">Portal (frontend)</dt>
              <dd><Commit sha={serving.frontend} short={serving.frontendShort} /></dd>
            </div>
            <div>
              <dt className="text-neutral-500">Production branch</dt>
              <dd><Commit sha={serving.branchHead} short={serving.branchHeadShort} /></dd>
            </div>
          </dl>
          {STATE_TEXT[serving.state] && (
            <p className="mt-3 flex items-start gap-2 text-sm text-amber-700 dark:text-amber-300">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {STATE_TEXT[serving.state]}
            </p>
          )}
          <p className="mt-3 text-xs text-neutral-500 dark:text-neutral-400">
            Every tenant runs the same code, so a rollback applies to all of them. To put one tenant’s data back, use Export and Restore on
            that tenant. To tell tenants about an incident, post a banner from the <Link to="/manage" className="underline">Tenants</Link> page.
          </p>
        </div>
      )}

      {active && <RunProgress runId={active.runId} url={active.url} onDone={() => queryClient.invalidateQueries({ queryKey: ['platform-releases'] })} />}

      {v?.configured && (
        <>
          <section className="space-y-2">
            <h2 className="text-sm font-semibold text-neutral-900 dark:text-white">Waiting to promote</h2>
            {v.waiting.length === 0 && <p className="text-sm text-neutral-500">Production is up to date with main.</p>}
            {v.waiting.map((c, i) => (
              <div key={c.sha} className={card}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0 text-sm">
                    <Commit short={c.short} /> <span className="text-neutral-900 dark:text-white">{c.title}</span>
                    <span className="ml-2 text-xs text-neutral-500">{fmt(c.date)}</span>
                    {!c.stagingPassed && <span className="ml-2 text-xs text-amber-600">not green on staging</span>}
                  </div>
                  {c.stagingPassed && (
                    <button type="button" className={button} disabled={running} onClick={() => setOpen(open === `promote:${c.sha}` ? null : `promote:${c.sha}`)}>
                      <ArrowUpCircle className="h-4 w-4" /> Promote{i === 0 ? ' (newest)' : ''}
                    </button>
                  )}
                </div>
                {open === `promote:${c.sha}` && <PromoteForm commit={c} onStart={run('promote')} pending={start.isPending} />}
                {i === 0 && v.waiting.length > 1 && open !== `promote:${c.sha}` && (
                  <p className="mt-1 text-xs text-neutral-500">Promoting a commit also brings every commit below it.</p>
                )}
              </div>
            ))}
          </section>

          <section className="space-y-2">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-neutral-900 dark:text-white">
              <History className="h-4 w-4" /> Releases production has served
            </h2>
            {v.history.length === 0 && <p className="text-sm text-neutral-500">No release tags yet. Promote writes one each time.</p>}
            {v.history.map((r) => {
              const servingAll = r.servingBackend && r.servingFrontend;
              return (
                <div key={r.tag} className={card}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0 text-sm">
                      <Commit short={r.short} /> <span className="text-neutral-900 dark:text-white">{r.title || r.tag}</span>
                      <span className="ml-2 text-xs text-neutral-500">released {fmt(r.releasedAt)}</span>
                      {servingAll && <span className="ml-2 rounded-full bg-green-50 px-2 py-0.5 text-xs text-green-700 dark:bg-green-900/30 dark:text-green-300">serving</span>}
                      {!servingAll && (r.servingBackend || r.servingFrontend) && (
                        <span className="ml-2 text-xs text-amber-600">serving the {r.servingBackend ? 'backend' : 'portal'}</span>
                      )}
                    </div>
                    {!servingAll && (
                      <button type="button" className={button} disabled={running} onClick={() => setOpen(open === `rollback:${r.sha}` ? null : `rollback:${r.sha}`)}>
                        <RotateCcw className="h-4 w-4" /> Roll back to this
                      </button>
                    )}
                  </div>
                  {open === `rollback:${r.sha}` && <RollbackForm release={r} onStart={run('rollback')} pending={start.isPending} />}
                </div>
              );
            })}
          </section>

          <section className={card}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-sm">
                <div className="font-medium text-neutral-900 dark:text-white">Redeploy the backend</div>
                <div className="text-neutral-500">
                  Rebuilds <Commit sha={serving?.backend} short={serving?.backendShort} />, the commit serving now, for a deploy that stalled or a
                  machine that needs a fresh release. It never moves production forward or back.
                </div>
              </div>
              {open === 'redeploy' ? (
                <div className="flex gap-2">
                  <button type="button" className={primary} disabled={start.isPending} onClick={() => run('redeploy')({})}>
                    {start.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Yes, redeploy
                  </button>
                  <button type="button" className={button} onClick={() => setOpen(null)}>Cancel</button>
                </div>
              ) : (
                <button type="button" className={button} disabled={running} onClick={() => setOpen('redeploy')}>
                  <RefreshCw className="h-4 w-4" /> Redeploy
                </button>
              )}
            </div>
          </section>

          <section className="space-y-2">
            <h2 className="text-sm font-semibold text-neutral-900 dark:text-white">Recent runs</h2>
            {running && <p className="text-sm text-amber-700 dark:text-amber-300">A run is in progress; the buttons wait until it finishes.</p>}
            {v.runs.length === 0 && <p className="text-sm text-neutral-500">None yet.</p>}
            <ul className="divide-y divide-neutral-200 rounded-xl border border-neutral-200 bg-white text-sm dark:divide-neutral-700 dark:border-neutral-700 dark:bg-neutral-800">
              {v.runs.map((r) => (
                <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2">
                  <span className="min-w-0 text-neutral-800 dark:text-neutral-100">{r.title}</span>
                  <span className="flex items-center gap-3">
                    <span className={RUN_STYLES[r.conclusion] || 'text-neutral-500'}>{runState(r)}</span>
                    <span className="text-xs text-neutral-500">{fmt(r.createdAt)}</span>
                    <a href={r.url} target="_blank" rel="noreferrer" className="text-primary-600 hover:underline">
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  </span>
                </li>
              ))}
            </ul>
          </section>
        </>
      )}

      <div className="rounded-lg border border-neutral-200 p-3 text-xs text-neutral-500 dark:border-neutral-700 dark:text-neutral-400">
        <span className="font-medium text-neutral-700 dark:text-neutral-200">If this console is down:</span> GitHub → Actions → <em>Roll back production</em> →
        Run workflow, with the release commit, the scope and a reason. It works from the GitHub phone app too, and does not need the portal.
      </div>
    </div>
  );
}
